import React from 'react';
import { Text, Heading } from '@/components/ui/design-system';
export const CookiePolicyContent = () => (
  <>
    <Heading level={2} className="heading-underline-animated pb-2 block">Introduction</Heading>
    <Text size="lg" className="mt-4 mb-5">
      This Cookie Policy explains how PAVISHA ("we", "us", or "our") uses cookies and similar 
      technologies to recognize you when you visit our website. It explains what these 
      technologies are and why we use them, as well as your rights to control our use of them.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">What Are Cookies?</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Cookies are small data files that are placed on your computer or mobile device when you 
      visit a website. Cookies are widely used by website owners to make their websites work, 
      or work more efficiently, as well as to provide reporting information.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      Cookies set by the website owner (in this case, PAVISHA) are called "first-party cookies". 
      Cookies set by parties other than the website owner are called "third-party cookies". Third-party 
      cookies enable third-party features or functionality to be provided on or through the website 
      (e.g., advertising, interactive content, and analytics).
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Why Do We Use Cookies?</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We use first-party and third-party cookies for several reasons. Some cookies are required 
      for technical reasons in order for our website to operate, and we refer to these as "essential" 
      or "strictly necessary" cookies. Other cookies also enable us to track and target the interests 
      of our users to enhance the experience on our website. Third parties serve cookies through our 
      website for advertising, analytics, and other purposes.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Types of Cookies We Use</Heading>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Essential Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      These cookies are strictly necessary to provide you with services available through our 
      website and to use some of its features, such as access to secure areas. Because these 
      cookies are strictly necessary to deliver the website, you cannot refuse them without 
      impacting how our website functions.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Performance Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      These cookies collect information about how visitors use our website, for instance which 
      pages visitors go to most often. We use this information to improve our website and your 
      browsing experience. These cookies do not collect information that identifies you personally.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Functionality Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      These cookies allow our website to remember choices you make (such as your preferred language 
      or the region you are in) and provide enhanced, more personal features. These cookies can also 
      be used to remember changes you have made to text size, fonts, and other parts of web pages 
      that you can customize.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">Targeting/Advertising Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      These cookies are used to deliver advertisements more relevant to you and your interests. They 
      are also used to limit the number of times you see an advertisement as well as help measure the 
      effectiveness of an advertising campaign. They are usually placed by advertising networks with 
      our permission.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">How to Control Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      You have the right to decide whether to accept or reject cookies. You can set or amend your 
      web browser controls to accept or refuse cookies. If you choose to reject cookies, you may 
      still use our website though your access to some functionality and areas of our website may 
      be restricted.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      Most web browsers allow some control of most cookies through the browser settings. To find 
      out more about cookies, including how to see what cookies have been set, visit 
      <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer">www.aboutcookies.org</a> 
      or <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer">www.allaboutcookies.org</a>.
    </Text>
    
    <Heading level={3} className="heading-underline-animated pb-2 block mt-8">How to Manage Cookies in Different Web Browsers</Heading>
    <Text size="lg" className="mt-4 mb-5">
      To find information relating to other browsers, visit the browser developer's website.
    </Text>
    <ul className="mt-4 mb-5">
      <li>
        <strong>Chrome:</strong> <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">https://support.google.com/chrome/answer/95647</a>
      </li>
      <li>
        <strong>Firefox:</strong> <a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer</a>
      </li>
      <li>
        <strong>Safari:</strong> <a href="https://support.apple.com/en-us/HT201265" target="_blank" rel="noopener noreferrer">https://support.apple.com/en-us/HT201265</a>
      </li>
      <li>
        <strong>Microsoft Edge:</strong> <a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09</a>
      </li>
    </ul>

    <Heading level={2} className="heading-underline-animated pb-2 block">Cookies That Have Been Set in the Past</Heading>
    <Text size="lg" className="mt-4 mb-5">
      If you have disabled one or more cookies, we may still use information collected from cookies 
      prior to your disabled preference being set; however, we will stop using the disabled cookie 
      to collect any further information.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Third-Party Cookies</Heading>
    <Text size="lg" className="mt-4 mb-5">
      Our website may use third-party services that use cookies, such as:
    </Text>
    <ul className="mt-4 mb-5">
      <li>Google Analytics (analytics service)</li>
      <li>Google Ads (advertising service)</li>
      <li>Facebook Pixel (advertising and analytics service)</li>
      <li>Payment processors</li>
    </ul>
    <Text size="lg" className="mt-4 mb-5">
      These third-party services have their own privacy policies and cookie policies which we 
      recommend you review.
    </Text>

    <Heading level={2} className="heading-underline-animated pb-2 block">Changes to This Cookie Policy</Heading>
    <Text size="lg" className="mt-4 mb-5">
      We may update this Cookie Policy from time to time to reflect changes to the cookies we use or 
      for other operational, legal, or regulatory reasons. Please therefore revisit this Cookie Policy 
      regularly to stay informed about our use of cookies and related technologies.
    </Text>
    <Text size="lg" className="mt-4 mb-5">
      The date at the top of this Cookie Policy indicates when it was last updated.
    </Text>
  </>
);
